import { useEffect, useState, type RefObject } from 'react';
import { useWindowSize } from 'react-use';

/**
 * Takes a component ref (and an optional container ref) and returns the scroll
 * `start` and `end` percentages that are relative to the total document progress.
 * Created for (optional) use in combination with framer-motion its useScroll.
 * @example
 * const { scrollYProgress } = useScroll();
 * const scrollProgress = useTransform(scrollYProgress, [start, end], [0, 1]);
 * @param ref a ref to the component element
 * @param containerRef a ref to the container element
 * @returns [start, end]: relative percentages to the start and end of your
 * element within the viewport
 */

export const useRefScrollProgress = (
  ref: RefObject<HTMLDivElement> | null,
  containerRef?: RefObject<HTMLElement>,
): [start: number | undefined, end: number | undefined] => {
  const { width, height } = useWindowSize();
  const [start, setStart] = useState<number>();
  const [end, setEnd] = useState<number>();

  useEffect(() => {
    if (!ref?.current) return;

    const rect = ref.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const offsetTop =
      rect.top +
      (containerRef?.current ? containerRef.current.scrollTop : scrollTop) -
      window.innerHeight / 2;

    setStart(offsetTop / document.body.clientHeight);
    setEnd((offsetTop + rect.height) / document.body.clientHeight);
  }, [ref, containerRef, width, height]);

  return [start, end];
};
